import {
  PodcastPage,
  PodcastPageQuery,
  PodcastPageQueryVariables,
  PodcastEntity,
  PodcastQuery,
  FetchLatestPodcastsQuery,
  FetchLatestPodcastsQueryVariables,
  FindPodcastListeningTimeQuery,
  FindPodcastListeningTimeQueryVariables,
  CreatePodcastInteractionMutation,
  CreatePodcastInteractionMutationVariables,
  PodcastInteractionInput,
  UpdatePodcastListeningTimeMutation,
  UpdatePodcastListeningTimeMutationVariables,
  CreatePodcastListeningTimeMutation,
  CreatePodcastListeningTimeMutationVariables,
  PodcastListeningTimeInput,
  PodcastListeningTimeEntity,
  FetchPodcastsPageableQuery,
  FetchPodcastsPageableQueryVariables,
} from '@/graphql/generated/graphql';
import requestHandler from '../requestHandler';
import { initializeApollo } from '../lib/apollo-client';
import {
  QUERY_FETCH_LATEST_PODCASTS,
  QUERY_FETCH_PODCAST_PAGE,
  QUERY_FETCH_PODCASTS_PAGEABLE,
  QUERY_FIND_LISTENING_TIME,
  QUERY_PODCAST,
} from '@/graphql/queries/podcast/podcast';
import { getAuthors } from '../knowledge-hub/utils';
import { getAuthentication } from '@/hooks/useAuthentication';
import {
  MUTATION_CREATE_PODCAST_INTERACTION,
  MUTATION_CREATE_PODCAST_LISTENING_TIME,
  MUTATION_UPDATE_PODCAST_LISTENING_TIME,
} from '@/graphql/mutations/podcast/podcast';

export const fetchPodcastPage = async () => {
  const apolloClient = initializeApollo();

  const { data, error } = await requestHandler(
    apolloClient.query<PodcastPageQuery, PodcastPageQueryVariables>({
      query: QUERY_FETCH_PODCAST_PAGE,
    }),
  );

  if (error) {
    console.error('Error fetching podcast page', error);
    return { page: null };
  }

  return {
    page: data?.podcastPage?.data?.attributes as PodcastPage,
  };
};

export const fetchPodcastPageable = async (page: number = 1) => {
  const apolloClient = initializeApollo();

  const { data, error } = await requestHandler(
    apolloClient.query<FetchPodcastsPageableQuery, FetchPodcastsPageableQueryVariables>({
      query: QUERY_FETCH_PODCASTS_PAGEABLE,
      variables: {
        page,
        userId: getAuthentication()?.user?.id,
      },
    }),
  );

  if (error) {
    console.error('Error fetching podcast', error);
    return { podcasts: null };
  }

  return {
    podcasts: data?.podcasts?.data,
    meta: data?.podcasts?.meta,
  };
};

export const fetchPodcast = async (slug: string) => {
  const apolloClient = initializeApollo();

  const { data, error } = await requestHandler(
    apolloClient.query<PodcastQuery>({
      query: QUERY_PODCAST,
      fetchPolicy: 'cache-first',
      variables: {
        slug: [slug],
        userId: getAuthentication()?.user?.id,
      },
    }),
  );

  if (error) {
    console.error('Error fetching podcast', error);
    return { podcast: null };
  }

  if (!data?.podcasts?.data.length) {
    return { podcast: null };
  }

  return {
    podcast: data?.podcasts?.data[0] as PodcastEntity | null,
  };
};

export const fetchRelatedContent = async (slug: string) => {
  const apolloClient = initializeApollo();

  const { data, error } = await requestHandler(
    apolloClient.query<FetchLatestPodcastsQuery, FetchLatestPodcastsQueryVariables>({
      query: QUERY_FETCH_LATEST_PODCASTS,
      fetchPolicy: 'cache-first',
      variables: {
        slug: [slug],
      },
    }),
  );

  if (error) {
    console.error('Error fetching podcast', error);
    return { podcast: null };
  }
  const relatedContent = data?.podcasts?.data.map((podcast) => {
    return {
      title: podcast.attributes?.title || '',
      description: podcast.attributes?.briefDescription || '',
      author: getAuthors(podcast as PodcastEntity),
      link: `/podcast/${podcast.attributes?.slug}`,
      type: 'Podcast',
      image: {
        url: podcast.attributes?.cover?.data?.attributes?.url || '',
        alternativeText: podcast.attributes?.cover?.data?.attributes?.alternativeText || '',
      },
    };
  });

  return {
    podcasts: relatedContent,
  };
};

export const findListeningTime = async (podcastId: string) => {
  const apolloClient = initializeApollo();

  const { data, error } = await requestHandler(
    apolloClient.query<FindPodcastListeningTimeQuery, FindPodcastListeningTimeQueryVariables>({
      query: QUERY_FIND_LISTENING_TIME,
      variables: {
        podcastId,
        userId: getAuthentication()?.user?.id,
      },
    }),
  );

  if (error) {
    console.error('Error listening time', error);
    return { listeningTime: null };
  }

  return {
    listeningTime: data?.podcastListeningTimes?.data.length
      ? (data?.podcastListeningTimes?.data?.[0] as PodcastListeningTimeEntity)
      : { id: null, attributes: { listeningTime: 0, duration: 0 } },
  };
};

export const createPodcastInteraction = async (inputData: PodcastInteractionInput) => {
  const apolloClient = initializeApollo();

  const { data, error } = await requestHandler(
    apolloClient.mutate<CreatePodcastInteractionMutation, CreatePodcastInteractionMutationVariables>({
      mutation: MUTATION_CREATE_PODCAST_INTERACTION,
      variables: {
        input: inputData,
      },
    }),
  );

  return { data, error };
};
export const createPodcastListeningTime = async (inputData: PodcastListeningTimeInput) => {
  const apolloClient = initializeApollo();

  const { data, error } = await requestHandler(
    apolloClient.mutate<CreatePodcastListeningTimeMutation, CreatePodcastListeningTimeMutationVariables>({
      mutation: MUTATION_CREATE_PODCAST_LISTENING_TIME,
      variables: {
        input: inputData,
      },
    }),
  );

  return { data, error };
};
export const updatePodcastListeningTime = async (id: string, inputData: PodcastListeningTimeInput) => {
  const apolloClient = initializeApollo();

  const { data, error } = await requestHandler(
    apolloClient.mutate<UpdatePodcastListeningTimeMutation, UpdatePodcastListeningTimeMutationVariables>({
      mutation: MUTATION_UPDATE_PODCAST_LISTENING_TIME,
      variables: {
        id,
        input: inputData,
      },
    }),
  );

  return { data, error };
};
